import type {
    SavedItineraryEntryDTO,
    SavedItineraryUpsertEntryDTO,
    UpsertSavedItinerariesRequestDTO,
} from "../types/dtos/api/saved-itineraries/saved-itinerary.dto";
import { authHttp } from "./auth/auth-http";

/**
 * Authenticated API client for the saved ("favorite") itineraries domain.
 *
 * The server list is the profile truth: `getSavedItineraries` rebuilds the
 * local saved-itinerary store on session start, `upsertSavedItineraries`
 * pushes each favorite toggle (and merges browser-local leftovers at login),
 * and `deleteSavedItinerary` removes an un-favorited itinerary. All calls are
 * idempotent so callers can fire-and-forget and reconcile at the next sync.
 */
export class SavedItinerariesApi {

    /**
     * Lists the authenticated user's saved itineraries.
     *
     * @returns Saved itinerary entries, newest `savedAt` first.
     */
    public static async getSavedItineraries(): Promise<SavedItineraryEntryDTO[]> {
        const response = await authHttp.get<SavedItineraryEntryDTO[]>( "/api/saved-itineraries" );

        return response.data;
    }

    /**
     * Upserts saved itineraries keyed by `itinerary.id` — a single favorite
     * toggle (one entry) or a browser-local merge (up to 20 entries).
     *
     * @param entries - Entries to insert or refresh in the profile.
     * @returns The full saved-itinerary list after the upsert.
     */
    public static async upsertSavedItineraries( entries: SavedItineraryUpsertEntryDTO[] ): Promise<SavedItineraryEntryDTO[]> {
        const payload: UpsertSavedItinerariesRequestDTO = { entries };
        const response = await authHttp.put<SavedItineraryEntryDTO[]>( "/api/saved-itineraries", payload );

        return response.data;
    }

    /**
     * Removes one saved itinerary of the authenticated user.
     *
     * @param itineraryId - Itinerary id (`itinerary.id`, format `it|…` — URL-encoded here).
     */
    public static async deleteSavedItinerary( itineraryId: string ): Promise<void> {
        await authHttp.delete( `/api/saved-itineraries/${ encodeURIComponent( itineraryId ) }` );
    }

}
